import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AirdropCheckResponse, claimAirdrop } from 'shared/api';
import { UICenteredModal } from 'shared/ui/components/modal/centered-modal/centered-modal';
import { UIButton } from 'shared/ui/components/button/button';
import { UITypography } from 'shared/ui/components/typography/typography';
import { formatLargeNumber } from 'shared/utils';
import ConfettiExplosion from 'react-confetti-explosion';
import { useMarketStore } from 'shared/store';
import { AmountWithTokenData } from 'components/bakery/amount';
import { USDT_TOKEN } from 'shared/constants/tokens';
import styles from './style.module.scss';

interface AirdropModalProps {
  isOpen: boolean;
  onClose: () => void;
  airdropData: AirdropCheckResponse['airdrop'];
  onSuccess?: () => void;
}

export const AirdropModal = ({ isOpen, onClose, airdropData, onSuccess }: AirdropModalProps) => {
  const { t, i18n } = useTranslation();
  const [claiming, setClaiming] = useState(false);
  const [showConfetti] = useState(true);
  const marketItems = useMarketStore((state) => state.marketItems);
  const currentLanguage = i18n.language;

  if (!airdropData) return null;

  // Helper function to get market item by type and level
  const getMarketItem = (type: 'skin_oven' | 'skin_hero', level: string | number) => {
    if (!marketItems || !marketItems[type]) {
      return null;
    }
    
    const items = marketItems[type] || {};
    return Object.values(items).find((item: any) => item.level === Number(level));
  };

  const handleClaim = async () => {
    setClaiming(true);
    try {
      console.log('Claiming airdrop...', airdropData);
      const response = await claimAirdrop();
      console.log('Claim response:', response.data);
      if (response.data.success) {
        console.log('Claim successful!');
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error('Failed to claim airdrop:', error);
    } finally {
      setClaiming(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <UICenteredModal
      visible={isOpen}
      onClose={onClose}
      showCloseButton={false}
      disableBackdropClick={true}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <UITypography variant="H2" className={styles.title}>
            🎁 {t('v2.airdrop.title')}
          </UITypography>
          {showConfetti && (
            <div className={styles.confettiWrapper}>
              <ConfettiExplosion
                zIndex={999999999}
                particleCount={150}
                duration={3000}
                force={0.8}
                width={600}
                colors={['#6ACEFE', '#FE49D8', '#FFC440', '#00FFD1', '#FF00FF']}
              />
            </div>
          )}
        </div>

        <div className={styles.content}>
          <UITypography variant="Body" className={styles.description}>
            {t('v2.airdrop.description')}
          </UITypography>

          <div className={styles.rewardsList}>
            {airdropData.reward_items.oven && (() => {
              const ovenItem = getMarketItem('skin_oven', airdropData.reward_items.oven.level);
              const ovenName = ovenItem?.properties?.name?.[currentLanguage] || 
                               ovenItem?.properties?.name?.en || 
                               `${t('v2.market.oven')} ${airdropData.reward_items.oven.level}`;
              
              return (
                <div className={styles.rewardItem}>
                  <div className={styles.itemIcon}>
                    <img 
                      src={`ovens/${airdropData.reward_items.oven.level}.webp`} 
                      alt={ovenName}
                      className={styles.icon}
                    />
                  </div>
                  <span className={styles.itemText}>
                    {ovenName} (Lvl {airdropData.reward_items.oven.level})
                  </span>
                </div>
              );
            })()}

            {airdropData.reward_items.bots &&
              airdropData.reward_items.bots.map((bot, index) => {
                const heroItem = getMarketItem('skin_hero', bot.level);
                const heroName = heroItem?.properties?.name?.[currentLanguage] || 
                                 heroItem?.properties?.name?.en || 
                                 `${t('v2.market.hero')} ${bot.level}`;
                
                return (
                  <div key={index} className={styles.rewardItem}>
                    <div className={styles.itemIcon}>
                      <img 
                        src={`heroes/${bot.level}.webp`} 
                        alt={heroName}
                        className={styles.icon}
                      />
                    </div>
                    <span className={styles.itemText}>
                      {heroName} (Lvl {bot.level}){bot.quantity > 1 ? `: x${bot.quantity}` : ''}
                    </span>
                  </div>
                );
              })}
          </div>

          <div className={styles.totalValue}>
            <span>{t('v2.airdrop.total_value')}:</span>
            <AmountWithTokenData 
              amount={String(airdropData.total_value_usdt)} 
              token={USDT_TOKEN}
              decimals={0}
              tokenTitle={false}
            />
          </div>

          <div className={styles.expiry}>
            <UITypography variant="Body" className={styles.expiryText}>
              {t('v2.airdrop.available_until')}: {formatDate(airdropData.expires_at)}
            </UITypography>
          </div>
        </div>

        <div className={styles.actions}>
          <UIButton
            onClick={handleClaim}
            loading={claiming}
            disabled={claiming}
            className={styles.claimButton}
          >
            {t('v2.staking.claim_rewards')}
          </UIButton>
        </div>
      </div>
    </UICenteredModal>
  );
};
